import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Wallet as WalletIcon, ArrowDownToLine, ArrowUpFromLine, RefreshCw,
  Loader2, CheckCircle2, Clock, XCircle, Copy, ArrowLeftRight, Send, Mail, ShieldCheck, RotateCw,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useMode } from "../context/ModeContext";
import { usd, timeAgo, signed, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import DepositDialog from "../components/DepositDialog";
import WithdrawForm from "../components/WithdrawForm";

const TX_LABEL = {
  trade_stake: "Trade stake", trade_win: "Trade payout", deposit: "Deposit",
  withdrawal_hold: "Withdrawal (hold)", withdrawal_refund: "Withdrawal refund",
  transfer_out: "Transfer sent", transfer_in: "Transfer received",
  transfer_hold: "Transfer (pending verification)", transfer_refund: "Transfer refund",
  admin_credit: "Account credit", admin_debit: "Account debit",
};


const StatusPill = ({ s }) => {
  const map = {
    completed: ["text-pos bg-pos/15", CheckCircle2], approved: ["text-pos bg-pos/15", CheckCircle2], pending: ["text-gold bg-gold/15", Clock],
    awaiting_verification: ["text-gold bg-gold/15", Clock], rejected: ["text-neg bg-neg/15", XCircle],
    failed: ["text-neg bg-neg/15", XCircle], cancelled: ["text-fx-s3 bg-white/5", XCircle],
  };
  const [cls, Icon] = map[s] || ["text-fx-s3 bg-white/5", Clock];
  return <span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono inline-flex items-center gap-1", cls)}><Icon className="h-3 w-3" /> {s.replace("_", " ")}</span>;
};

export default function WalletPage() {
  const { account, refreshAccount } = useMode();
  const [txns, setTxns] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [wd, setWd] = useState({ amount_usd: 50, method_id: "", details: {} });
  const [wdMethods, setWdMethods] = useState([]);
  const [otpOn, setOtpOn] = useState(true);
  useEffect(() => { api.get("/payments/otp-status").then((r) => setOtpOn(r.data.otp_email_enabled)).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  // Withdrawal OTP flow
  const [wdStep, setWdStep] = useState("form"); // form | otp | done
  const [wdChallenge, setWdChallenge] = useState(null);
  const [wdResult, setWdResult] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const [showTransfer, setShowTransfer] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [tf, setTf] = useState({ identifier: "", amount_usd: 50 });
  const [tfRecipient, setTfRecipient] = useState(null);
  const [tfStep, setTfStep] = useState("form"); // form | otp | done
  const [tfChallenge, setTfChallenge] = useState(null);
  const [tfResult, setTfResult] = useState(null);
  const [tfOtp, setTfOtp] = useState("");
  const [tfOtpError, setTfOtpError] = useState("");
  const [tfExpiresAt, setTfExpiresAt] = useState(0);
  const [tfResendAt, setTfResendAt] = useState(0);
  const [params, setParams] = useSearchParams();
  const polled = useRef(false);

  const loadAll = useCallback(async () => {
    const [t, d, w, tr] = await Promise.all([
      api.get("/wallet/transactions"), api.get("/deposits"), api.get("/withdrawals"), api.get("/wallet/transfers"),
    ]);
    setTxns(t.data); setDeposits(d.data); setWithdrawals(w.data); setTransfers(tr.data);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Deep-link actions from mobile bottom nav (?action=deposit|withdraw)
  useEffect(() => {
    const action = params.get("action");
    if (action === "deposit") { setShowDeposit(true); setParams({}, { replace: true }); }
    else if (action === "withdraw") { setShowWithdraw(true); setParams({}, { replace: true }); }
    // eslint-disable-next-line
  }, [params]);

  // Handle Stripe / provider return
  useEffect(() => {
    const sid = params.get("session_id");
    const prov = params.get("provider");
    const ref = params.get("reference");
    const cancelled = params.get("payment");
    if (cancelled === "cancelled") { toast.error("Payment cancelled"); setParams({}, { replace: true }); return; }
    if (sid && !polled.current) {
      polled.current = true;
      pollStripe(sid);
    } else if (prov && ref && !polled.current) {
      polled.current = true;
      verifyProvider(prov, ref);
    }
    // eslint-disable-next-line
  }, [params]);

  const pollStripe = async (sid, attempt = 0) => {
    try {
      const { data } = await api.get(`/payments/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success(`Deposit of ${usd(data.amount)} completed!`);
        await refreshAccount(); loadAll();
        setParams({}, { replace: true });
        return;
      }
    } catch {}
    if (attempt < 6) setTimeout(() => pollStripe(sid, attempt + 1), 2000);
    else { toast.message("Payment still processing — it will reflect shortly."); setParams({}, { replace: true }); }
  };

  const verifyProvider = async (prov, ref) => {
    try {
      const { data } = await api.get(`/payments/${prov}/verify/${ref}`);
      if (data.status === "completed") { toast.success(`Deposit of ${usd(data.amount)} completed!`); await refreshAccount(); loadAll(); }
      else toast.message("Payment pending confirmation.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed"); }
    setParams({}, { replace: true });
  };


  // tick for OTP countdown while on a verification step
  useEffect(() => {
    if (wdStep !== "otp" && tfStep !== "otp") return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [wdStep, tfStep]);

  const submitWithdraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/withdrawals", { ...wd, amount_usd: Number(wd.amount_usd) });
      if (data.status === "pending") {
        setWdResult(data); setWdStep("done"); toast.success("Withdrawal submitted for approval");
        await refreshAccount(); loadAll(); return;
      }
      setWdChallenge(data);
      setWdStep("otp");
      setOtpCode(""); setOtpError("");
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setResendAt(Date.now() + (data.resend_in || 60) * 1000);
      toast.success("Verification code sent to your email");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Withdrawal failed");
    } finally { setBusy(false); }
  };

  const verifyWithdrawOtp = async () => {
    if (!/^\d{6}$/.test(otpCode)) { setOtpError("Enter the 6-digit code"); return; }
    setBusy(true); setOtpError("");
    try {
      const { data } = await api.post(`/withdrawals/${wdChallenge.id}/verify-otp`, { code: otpCode });
      setWdResult(data);
      setWdStep("done");
      await refreshAccount(); loadAll();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Verification failed";
      setOtpError(msg);
      // If the request was cancelled server-side (locked), reset to form
      if (/cancelled|start again/i.test(msg)) { setWdStep("form"); setWdChallenge(null); await refreshAccount(); loadAll(); }
    } finally { setBusy(false); }
  };

  const resendWithdrawOtp = async () => {
    setBusy(true); setOtpError("");
    try {
      const { data } = await api.post(`/withdrawals/${wdChallenge.id}/resend-otp`);
      setOtpCode("");
      setOtpExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setResendAt(Date.now() + (data.resend_in || 60) * 1000);
      toast.success("A new code was sent to your email");
    } catch (e) {
      setOtpError(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend code");
    } finally { setBusy(false); }
  };

  const cancelWithdrawOtp = async () => {
    if (wdChallenge?.id && wdStep === "otp") {
      try { await api.post(`/withdrawals/${wdChallenge.id}/cancel`); } catch { /* */ }
      await refreshAccount(); loadAll();
    }
  };

  const closeWithdraw = async (open) => {
    if (open) { setShowWithdraw(true); return; }
    // closing: cancel an in-progress OTP challenge to release held funds
    if (wdStep === "otp") await cancelWithdrawOtp();
    setShowWithdraw(false);
    setTimeout(() => { setWdStep("form"); setWdChallenge(null); setWdResult(null); setOtpCode(""); setOtpError(""); }, 200);
  };

  const resetDemo = async () => { await api.post("/wallet/demo/reset"); await refreshAccount(); loadAll(); toast.success("Demo wallet reset"); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const demoBal = account?.demo_wallet?.balance ?? 0;
  const liveBal = account?.live_wallet?.balance ?? 0;
  const expiresIn = Math.max(0, Math.floor((otpExpiresAt - nowTick) / 1000));
  const resendIn = Math.max(0, Math.ceil((resendAt - nowTick) / 1000));
  const tfExpiresIn = Math.max(0, Math.floor((tfExpiresAt - nowTick) / 1000));
  const tfResendIn = Math.max(0, Math.ceil((tfResendAt - nowTick) / 1000));
  const cancelAndReset = async () => {
    await cancelWithdrawOtp();
    setWdStep("form"); setWdChallenge(null); setOtpCode(""); setOtpError("");
  };

  const lookupRecipient = async () => {
    setTfRecipient(null);
    if (!tf.identifier.trim()) return;
    try {
      const { data } = await api.get("/wallet/transfer/lookup", { params: { identifier: tf.identifier.trim() } });
      if (data.found) setTfRecipient(data);
      else { setTfRecipient(false); toast.error("No matching customer found"); }
    } catch { setTfRecipient(false); }
  };

  const submitTransfer = async () => {
    if (!tfRecipient) { toast.error("Confirm the recipient first"); return; }
    if (!tf.amount_usd || tf.amount_usd <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/transfer", { identifier: tf.identifier.trim(), amount_usd: Number(tf.amount_usd) });
      if (data.status === "completed") {
        setTfResult(data); setTfStep("done"); toast.success("Transfer completed");
        await refreshAccount(); loadAll(); return;
      }
      setTfChallenge(data); setTfStep("otp"); setTfOtp(""); setTfOtpError("");
      setTfExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setTfResendAt(Date.now() + (data.resend_in || 60) * 1000);
      toast.success("Verification code sent to your email");
      await refreshAccount();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transfer failed");
    } finally { setBusy(false); }
  };

  const verifyTransferOtp = async () => {
    if (!/^\d{6}$/.test(tfOtp)) { setTfOtpError("Enter the 6-digit code"); return; }
    setBusy(true); setTfOtpError("");
    try {
      const { data } = await api.post(`/wallet/transfer/${tfChallenge.id}/verify-otp`, { code: tfOtp });
      setTfResult(data); setTfStep("done");
      await refreshAccount(); loadAll();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Verification failed";
      setTfOtpError(msg);
      if (/cancelled|start again/i.test(msg)) { setTfStep("form"); setTfChallenge(null); await refreshAccount(); loadAll(); }
    } finally { setBusy(false); }
  };

  const resendTransferOtp = async () => {
    setBusy(true); setTfOtpError("");
    try {
      const { data } = await api.post(`/wallet/transfer/${tfChallenge.id}/resend-otp`);
      setTfOtp("");
      setTfExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
      setTfResendAt(Date.now() + (data.resend_in || 60) * 1000);
      toast.success("A new code was sent to your email");
    } catch (e) {
      setTfOtpError(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend code");
    } finally { setBusy(false); }
  };

  const cancelTransferOtp = async () => {
    if (tfChallenge?.id && tfStep === "otp") {
      try { await api.post(`/wallet/transfer/${tfChallenge.id}/cancel`); } catch { /* */ }
      await refreshAccount(); loadAll();
    }
  };

  const closeTransfer = async (open) => {
    if (open) { setShowTransfer(true); return; }
    if (tfStep === "otp") await cancelTransferOtp();
    setShowTransfer(false);
    setTimeout(() => { setTf({ identifier: "", amount_usd: 50 }); setTfRecipient(null); setTfStep("form"); setTfChallenge(null); setTfResult(null); setTfOtp(""); setTfOtpError(""); }, 200);
  };
  const cancelTransferAndReset = async () => {
    await cancelTransferOtp();
    setTfStep("form"); setTfChallenge(null); setTfOtp(""); setTfOtpError("");
  };

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-[1200px]">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Wallet</h1>
        <p className="text-fx-s3 mt-1">Fund your live account to unlock real trading features. Demo and live balances stay separate.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-brandblue/30 bg-gradient-to-br from-brandblue/15 to-fx-s1 p-6" data-testid="wallet-demo-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase tracking-widest text-brandblue">Demo Account · USD</span>
            <WalletIcon className="h-5 w-5 text-brandblue" />
          </div>
          <div className="font-mono text-4xl font-bold mt-4">{usd(demoBal)}</div>
          <p className="text-xs text-fx-s3 mt-2">Virtual funds. Cannot be withdrawn or converted.</p>
          <Button onClick={resetDemo} data-testid="wallet-reset-demo-btn" variant="outline" className="mt-4 border-white/10 bg-fx-base gap-2"><RefreshCw className="h-4 w-4" /> Reset balance</Button>
        </div>

        <div className="rounded-xl border border-gold/30 bg-gradient-to-br from-gold/10 to-fx-s1 p-6" data-testid="wallet-live-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">Live Account · USD</span>
            <WalletIcon className="h-5 w-5 text-gold" />
          </div>
          <div className="font-mono text-4xl font-bold mt-4">{usd(liveBal)}</div>
          <p className="text-xs text-fx-s3 mt-2">Real funds. Deposits credit instantly (card) or after admin verification (crypto/manual).</p>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setShowDeposit(true)} data-testid="deposit-btn" className="bg-gold hover:brightness-110 text-black gap-2 flex-1"><ArrowDownToLine className="h-4 w-4" /> Deposit</Button>
            <Button onClick={() => setShowWithdraw(true)} data-testid="withdraw-btn" variant="outline" className="border-white/10 bg-fx-base gap-2 flex-1"><ArrowUpFromLine className="h-4 w-4" /> Withdraw</Button>
            <Button onClick={() => setShowTransfer(true)} data-testid="transfer-btn" variant="outline" className="border-white/10 bg-fx-base gap-2 flex-1"><ArrowLeftRight className="h-4 w-4" /> Transfer</Button>
          </div>
        </div>
      </div>

      {/* Deposits & Withdrawals */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-fx-s1 border border-white/5">
          <div className="px-5 py-4 border-b border-white/5"><h2 className="font-display font-bold">Deposits</h2></div>
          <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
            {deposits.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No deposits yet.</div>}
            {deposits.map((d) => (
              <div key={d.id} data-testid={`deposit-${d.id}`} className="flex items-center justify-between px-5 py-3">
                <div><div className="text-sm font-medium capitalize">{d.provider}{d.coin ? ` · ${d.coin}` : d.bank_name ? ` · ${d.bank_name}` : ""}</div><div className="text-xs text-fx-s3">{timeAgo(d.created_at)}</div></div>
                <div className="text-right"><div className="font-mono font-semibold text-pos">+{usd(d.amount_usd)}</div><StatusPill s={d.status} /></div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl bg-fx-s1 border border-white/5" data-testid="wallet-withdrawals">
          <div className="px-5 py-4 border-b border-white/5"><h2 className="font-display font-bold">Withdrawals</h2></div>
          <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
            {withdrawals.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No withdrawals yet.</div>}
            {withdrawals.map((w) => (
              <div key={w.id} data-testid={`withdrawal-${w.id}`} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0"><div className="text-sm font-medium truncate">{w.method}</div><div className="text-xs text-fx-s3 truncate">{w.destination} · {timeAgo(w.created_at)}</div></div>
                <div className="text-right shrink-0"><div className="font-mono font-semibold text-neg">-{usd(w.amount_usd)}</div>{w.fee_usd ? <div className="text-[10px] text-fx-s3">receive {usd(w.net_amount_usd)}</div> : null}<StatusPill s={w.status} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Transfers */}
      <div className="rounded-xl bg-fx-s1 border border-white/5" data-testid="wallet-transfers">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-brandblue" /><h2 className="font-display font-bold">Transfers</h2></div>
        <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
          {transfers.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No transfers yet.</div>}
          {transfers.map((t) => (
            <div key={t.id} data-testid={`transfer-${t.id}`} className="flex items-center justify-between px-5 py-3">
              <div><div className="text-sm font-medium">{t.direction === "out" ? "Sent to" : "Received from"} {t.counterparty}</div><div className="text-xs text-fx-s3">{timeAgo(t.created_at)}</div></div>
              <div className={clsx("font-mono font-semibold", t.direction === "out" ? "text-neg" : "text-pos")}>{t.direction === "out" ? "-" : "+"}{usd(t.amount_usd)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Transactions */}
      <div className="rounded-xl bg-fx-s1 border border-white/5">
        <div className="px-5 py-4 border-b border-white/5"><h2 className="font-display font-bold">Transaction History</h2></div>
        <div className="divide-y divide-white/[0.03] max-h-80 overflow-y-auto">
          {txns.length === 0 && <div className="px-5 py-10 text-center text-fx-s3 text-sm">No transactions yet.</div>}
          {txns.map((t) => (
            <div key={t.id} data-testid={`txn-${t.id}`} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-3">
                <div className={clsx("h-9 w-9 rounded-lg grid place-items-center", t.amount >= 0 ? "bg-pos/15 text-pos" : "bg-neg/15 text-neg")}>
                  {t.amount >= 0 ? <ArrowDownToLine className="h-4 w-4" /> : <ArrowUpFromLine className="h-4 w-4" />}
                </div>
                <div>
                  <div className="text-sm font-medium">{TX_LABEL[t.type] || t.type}</div>
                  <div className="text-xs text-fx-s3"><span className={t.mode === "demo" ? "text-brandblue" : "text-gold"}>{t.mode.toUpperCase()}</span> · {timeAgo(t.created_at)}</div>
                </div>
              </div>
              <span className={clsx("font-mono font-semibold", t.amount >= 0 ? "text-pos" : "text-neg")}>{signed(t.amount)}</span>
            </div>
          ))}
        </div>
      </div>

      <DepositDialog open={showDeposit} onOpenChange={setShowDeposit} onDone={loadAll} />

      {/* Withdraw dialog — email OTP verified */}
      <Dialog open={showWithdraw} onOpenChange={closeWithdraw}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-md" data-testid="withdraw-dialog">
          <DialogHeader><DialogTitle className="font-display">
            {wdStep === "form" && "Withdraw from Live Wallet"}
            {wdStep === "otp" && "Verify Your Withdrawal"}
            {wdStep === "done" && "Withdrawal Submitted"}
          </DialogTitle></DialogHeader>

          {wdStep === "form" && (
            <>
              <WithdrawForm wd={wd} setWd={setWd} liveBal={liveBal} onMethods={setWdMethods} />
              {wdMethods.length > 0 && <p className="text-xs text-fx-s3" data-testid="withdraw-security-note">{otpOn ? "For your security, we'll email a verification code to confirm this withdrawal. Funds are held after verification and released on admin approval." : "Funds are held when you submit and released on admin approval."}</p>}
              <DialogFooter><Button onClick={submitWithdraw} disabled={busy || !wdMethods.length} data-testid="withdraw-confirm-btn" className="bg-brandblue hover:bg-blue-500 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request Withdrawal"}</Button></DialogFooter>
            </>
          )}

          {wdStep === "otp" && wdChallenge && (
            <>
              <div className="space-y-4" data-testid="withdraw-otp-step">
                <div className="flex items-start gap-3 rounded-lg bg-brandblue/10 border border-brandblue/25 p-3">
                  <Mail className="h-5 w-5 text-brandblue shrink-0 mt-0.5" />
                  <p className="text-sm text-fx-s2 text-white/90">We sent a 6-digit verification code to <b data-testid="withdraw-otp-email">{wdChallenge.email_masked}</b>. Enter it below to complete your withdrawal of <b>{usd(wdChallenge.amount_usd)}</b>{wdChallenge.fee_usd ? <> (you receive <b>{usd(wdChallenge.net_amount_usd)}</b> after a {usd(wdChallenge.fee_usd)} fee)</> : null}.</p>
                </div>
                <div className="space-y-1">
                  <Label>Verification code</Label>
                  <Input data-testid="withdraw-otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                    value={otpCode} onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(""); }}
                    placeholder="••••••" className="bg-fx-base border-white/10 font-mono text-center text-2xl tracking-[0.5em] h-14" />
                  {otpError && <p role="alert" data-testid="withdraw-otp-error" className="text-sm text-neg">{otpError}</p>}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-fx-s3" data-testid="withdraw-otp-countdown">
                    {expiresIn > 0 ? <>Code expires in <span className="font-mono text-white">{Math.floor(expiresIn / 60)}:{String(expiresIn % 60).padStart(2, "0")}</span></> : <span className="text-neg">Code expired — request a new one</span>}
                  </span>
                  <button onClick={resendWithdrawOtp} disabled={busy || resendIn > 0} data-testid="withdraw-otp-resend"
                    className={clsx("inline-flex items-center gap-1 font-medium", resendIn > 0 ? "text-fx-s3 cursor-not-allowed" : "text-brandblue hover:underline")}>
                    <RotateCw className="h-3 w-3" /> {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </button>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={cancelAndReset} disabled={busy} data-testid="withdraw-otp-cancel" className="border-white/10 bg-fx-base">Cancel</Button>
                <Button onClick={verifyWithdrawOtp} disabled={busy || otpCode.length !== 6 || expiresIn <= 0} data-testid="withdraw-otp-verify" className="bg-brandblue hover:bg-blue-500 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Verify OTP & Complete Withdrawal</>}</Button>
              </DialogFooter>
            </>
          )}

          {wdStep === "done" && wdResult && (
            <>
              <div className="space-y-4 text-center" data-testid="withdraw-done-step">
                <div className="mx-auto h-14 w-14 rounded-full bg-pos/15 grid place-items-center"><CheckCircle2 className="h-8 w-8 text-pos" /></div>
                <div>
                  <div className="font-display text-lg font-bold">Withdrawal submitted for approval</div>
                  <p className="text-sm text-fx-s3 mt-1">Your request {wdResult.otp_required === false ? "was submitted" : "was verified"} and is now pending administrator approval. You'll be notified once it's processed.</p>
                </div>
                <div className="rounded-lg bg-fx-base border border-white/5 p-3 text-left text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-fx-s3">Reference</span><span className="font-mono text-xs" data-testid="withdraw-ref">{wdResult.reference}</span></div>
                  <div className="flex justify-between"><span className="text-fx-s3">Amount</span><span className="font-mono">{usd(wdResult.amount_usd)}</span></div>
                  <div className="flex justify-between"><span className="text-fx-s3">Method</span><span>{wdResult.method}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-fx-s3">Destination</span><span className="font-mono text-xs truncate">{wdResult.destination}</span></div>
                </div>
              </div>
              <DialogFooter><Button onClick={() => closeWithdraw(false)} data-testid="withdraw-done-close" className="bg-brandblue hover:bg-blue-500">View withdrawal history</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer dialog — email OTP verified */}
      <Dialog open={showTransfer} onOpenChange={closeTransfer}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-md" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-brandblue" />
            {tfStep === "form" && "Transfer to another user"}
            {tfStep === "otp" && "Verify Your Transfer"}
            {tfStep === "done" && "Transfer Complete"}
          </DialogTitle></DialogHeader>

          {tfStep === "form" && (
            <>
              <div className="space-y-4">
                <div className="rounded-lg bg-fx-base border border-white/5 p-3 flex justify-between"><span className="text-sm text-fx-s3">Available (Live)</span><span className="font-mono">{usd(liveBal)}</span></div>
                <div className="space-y-1">
                  <Label>Recipient email or referral code</Label>
                  <div className="flex gap-2">
                    <Input data-testid="transfer-identifier-input" value={tf.identifier} onChange={(e) => { setTf({ ...tf, identifier: e.target.value }); setTfRecipient(null); }} placeholder="user@example.com or Fx Cloud code" className="bg-fx-base border-white/10" />
                    <Button onClick={lookupRecipient} data-testid="transfer-lookup-btn" variant="outline" className="border-white/10 bg-fx-base shrink-0">Find</Button>
                  </div>
                </div>
                {tfRecipient && (
                  <div className="rounded-lg bg-pos/10 border border-pos/30 p-3 text-sm" data-testid="transfer-recipient-confirm">
                    <div className="flex items-center gap-2 text-pos"><CheckCircle2 className="h-4 w-4" /> Sending to</div>
                    <div className="font-semibold mt-1">{tfRecipient.name}</div>
                    <div className="text-xs text-fx-s3 font-mono">{tfRecipient.email}</div>
                  </div>
                )}
                <div className="space-y-1"><Label>Amount (USD)</Label><Input data-testid="transfer-amount-input" type="number" min={1} value={tf.amount_usd} onChange={(e) => setTf({ ...tf, amount_usd: Number(e.target.value) })} className="bg-fx-base border-white/10 font-mono" /></div>
                <p className="text-xs text-fx-s3" data-testid="transfer-security-note">{otpOn ? "For your security, we'll email a verification code to confirm this transfer. Funds reach the recipient only after you verify." : "Transfers are sent instantly when you confirm."}</p>
              </div>
              <DialogFooter><Button onClick={submitTransfer} disabled={busy || !tfRecipient} data-testid="transfer-confirm-btn" className="bg-brandblue hover:bg-blue-500 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Request Transfer</>}</Button></DialogFooter>
            </>
          )}

          {tfStep === "otp" && tfChallenge && (
            <>
              <div className="space-y-4" data-testid="transfer-otp-step">
                <div className="flex items-start gap-3 rounded-lg bg-brandblue/10 border border-brandblue/25 p-3">
                  <Mail className="h-5 w-5 text-brandblue shrink-0 mt-0.5" />
                  <p className="text-sm text-white/90">We sent a 6-digit code to <b data-testid="transfer-otp-email">{tfChallenge.email_masked}</b>. Enter it to send <b>{usd(tfChallenge.amount_usd)}</b> to <b>{tfChallenge.recipient_name || tfChallenge.recipient_masked}</b>.</p>
                </div>
                <div className="space-y-1">
                  <Label>Verification code</Label>
                  <Input data-testid="transfer-otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                    value={tfOtp} onChange={(e) => { setTfOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setTfOtpError(""); }}
                    placeholder="••••••" className="bg-fx-base border-white/10 font-mono text-center text-2xl tracking-[0.5em] h-14" />
                  {tfOtpError && <p role="alert" data-testid="transfer-otp-error" className="text-sm text-neg">{tfOtpError}</p>}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-fx-s3" data-testid="transfer-otp-countdown">
                    {tfExpiresIn > 0 ? <>Code expires in <span className="font-mono text-white">{Math.floor(tfExpiresIn / 60)}:{String(tfExpiresIn % 60).padStart(2, "0")}</span></> : <span className="text-neg">Code expired — request a new one</span>}
                  </span>
                  <button onClick={resendTransferOtp} disabled={busy || tfResendIn > 0} data-testid="transfer-otp-resend"
                    className={clsx("inline-flex items-center gap-1 font-medium", tfResendIn > 0 ? "text-fx-s3 cursor-not-allowed" : "text-brandblue hover:underline")}>
                    <RotateCw className="h-3 w-3" /> {tfResendIn > 0 ? `Resend in ${tfResendIn}s` : "Resend code"}
                  </button>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={cancelTransferAndReset} disabled={busy} data-testid="transfer-otp-cancel" className="border-white/10 bg-fx-base">Cancel</Button>
                <Button onClick={verifyTransferOtp} disabled={busy || tfOtp.length !== 6 || tfExpiresIn <= 0} data-testid="transfer-otp-verify" className="bg-brandblue hover:bg-blue-500 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Verify OTP & Complete Transfer</>}</Button>
              </DialogFooter>
            </>
          )}

          {tfStep === "done" && tfResult && (
            <>
              <div className="space-y-4 text-center" data-testid="transfer-done-step">
                <div className="mx-auto h-14 w-14 rounded-full bg-pos/15 grid place-items-center"><CheckCircle2 className="h-8 w-8 text-pos" /></div>
                <div>
                  <div className="font-display text-lg font-bold">Transfer complete</div>
                  <p className="text-sm text-fx-s3 mt-1">Your funds were sent successfully. Both accounts received a receipt.</p>
                </div>
                <div className="rounded-lg bg-fx-base border border-white/5 p-3 text-left text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-fx-s3">Reference</span><span className="font-mono text-xs" data-testid="transfer-ref">{tfResult.reference}</span></div>
                  <div className="flex justify-between"><span className="text-fx-s3">Amount</span><span className="font-mono">{usd(tfResult.amount_usd)}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-fx-s3">Recipient</span><span className="truncate">{tfResult.recipient_name || tfResult.recipient_masked}</span></div>
                </div>
              </div>
              <DialogFooter><Button onClick={() => closeTransfer(false)} data-testid="transfer-done-close" className="bg-brandblue hover:bg-blue-500">Done</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
